import React, { useEffect, useMemo, useState } from 'react';
import { showNotification } from '../../../App';
import { retrieveEnquirySourceData } from '../../radio/ActionTypeRadio';
import { CannedNotesForField, CannedNotesForFieldProps } from './CannedNotesForField';
import { CannedNote } from './cannedNotes';
import { retrieveCannedNotes } from './cannedNotesRepository';

// The notes of the last load are kept here, so a form that opens later shows them straight away while the fresh ones are fetched (and the several
// notes fields on one enquiry share a single request).
let loadedCannedNotes: CannedNote[] | null = null;
let pendingCannedNotes: Promise<CannedNote[]> | null = null;

export const prefetchCannedNotes = (): Promise<CannedNote[]> => {
    if (!pendingCannedNotes) {
        pendingCannedNotes = retrieveCannedNotes()
            .then((cannedNotes) => {
                loadedCannedNotes = cannedNotes;
                pendingCannedNotes = null;
                return cannedNotes;
            })
            .catch((reason) => {
                pendingCannedNotes = null;
                showNotification(null, reason, 'error');
                return loadedCannedNotes || [];
            });
    }
    return pendingCannedNotes;
};

// The follow-up action types by name: the form holds the selected action's name, the canned notes belong to its guid. The list is the one the action
// chips use (cache-first, so no extra request).
let loadedActionTypeGuids: { [actionName: string]: string } | null = null;

const loadActionTypeGuids = (): Promise<{ [actionName: string]: string }> => new Promise((resolve) => {
    if (loadedActionTypeGuids) {
        resolve(loadedActionTypeGuids);
        return;
    }
    // Without the guids the notes are simply not reordered, so a failure here is not worth a message
    try {
        retrieveEnquirySourceData(
            false,
            (data) => {
                const guidsByName: { [actionName: string]: string } = {};
                data.lookup.actionTypes.forEach((actionType) => {
                    guidsByName[actionType.description] = actionType.guidId;
                });
                loadedActionTypeGuids = guidsByName;
                resolve(guidsByName);
            },
            () => resolve({}),
        );
    } catch (error) {
        resolve({});
    }
});

export const useCannedNotes = (selectedActionName?: string | null) => {
    const [cannedNotes, setCannedNotes] = useState<CannedNote[]>(loadedCannedNotes || []);
    const [isLoading, setIsLoading] = useState<boolean>(loadedCannedNotes === null);
    const [actionTypeGuids, setActionTypeGuids] = useState<{ [actionName: string]: string }>(loadedActionTypeGuids || {});

    useEffect(() => {
        let isMounted = true;
        prefetchCannedNotes().then((freshNotes) => {
            if (isMounted) {
                setCannedNotes(freshNotes);
                setIsLoading(false);
            }
        });
        loadActionTypeGuids().then((guidsByName) => {
            if (isMounted) {
                setActionTypeGuids(guidsByName);
            }
        });
        return () => {
            isMounted = false;
        };
    }, []);

    const selectedActionTypeGuid = useMemo(
        () => (selectedActionName ? actionTypeGuids[selectedActionName] : undefined),
        [selectedActionName, actionTypeGuids],
    );

    return { cannedNotes, isLoading, selectedActionTypeGuid };
};

interface ConnectedCannedNotesForFieldProps extends Omit<CannedNotesForFieldProps, 'notes' | 'isLoading' | 'selectedActionTypeGuid'> {
    // the follow-up action selected on the form (its name); its notes are listed first
    selectedActionName?: string | null;
}

// CannedNotesForField with the firm's notes loaded for it
export const ConnectedCannedNotesForField: React.FC<ConnectedCannedNotesForFieldProps> = ({ selectedActionName, ...fieldProps }) => {
    const { cannedNotes, isLoading, selectedActionTypeGuid } = useCannedNotes(selectedActionName);
    return <CannedNotesForField {...fieldProps} notes={cannedNotes} isLoading={isLoading} selectedActionTypeGuid={selectedActionTypeGuid} />;
};
